import { useSessionUser } from "hooks/useSession";
import * as React from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "components/common/buttons/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from "components/common/dropdown/DropDownV2";
import { useAuth } from "store/hooks/useAuth";
import { PATH_DASHBOARD } from "config";
import { helpArticleUrls } from "config/helpArticleUrls";
import { linkType } from "store/types/enum";
import { Bell, CircleHelp, Gift, LogOut, Settings } from "lucide-react";
import { UserAvatar } from "components/common/UserAvatar";
import { UserProfileInfo } from "components/common/UserProfileInfo";
import { ProfileDropdownLink, UserProfileDropdownProps } from "./UserProfileDropdown.types";
import { AppearanceThemeMenu } from "./components/AppearanceThemeMenu";
import { useNotificationCounts } from "hooks/useNotifications";

interface UseUserProfileMenuOptions {
  additionalLinks?: ProfileDropdownLink[];
  showBilling?: boolean;
}

/** Everything the account menu needs: who is signed in, its links and actions. */
export function useUserProfileMenu({
  additionalLinks = [],
  showBilling = false,
}: UseUserProfileMenuOptions = {}) {
  const { session: profile, isOneTimePurchase } = useSessionUser();
  const displayName = profile
    ? `${(profile.first_name || "").trim()} ${(profile.last_name || "").trim()}`.trim() ||
      profile.email?.split("@")[0] ||
      "Account"
    : "";
  const displayEmail = profile?.email ?? "";
  const { handleLogOut } = useAuth();
  const navigate = useNavigate();

  // The server's unread total; the badge needs no list.
  const { counts: notificationCounts } = useNotificationCounts();
  const unreadCount = notificationCounts.unread;
  const hasUnread = unreadCount > 0;
  const displayCount = unreadCount > 99 ? "99+" : unreadCount;

  const defaultProfileDropdownLinks: ProfileDropdownLink[] = [
    {
      name: "Notifications",
      link: PATH_DASHBOARD.notifications,
      link_type: linkType.INTERNAL,
      icon: <Bell className="h-4 w-4" />,
    },
    {
      name: "Settings",
      link: PATH_DASHBOARD.settings,
      link_type: linkType.INTERNAL,
      icon: <Settings className="h-4 w-4" />,
    },
    {
      name: "Referral",
      link: PATH_DASHBOARD.referrals,
      link_type: linkType.INTERNAL,
      icon: <Gift className="h-4 w-4" />,
    },
  ];

  const billingLink: ProfileDropdownLink | null =
    showBilling && !isOneTimePurchase
      ? {
          name: "Billing",
          link: PATH_DASHBOARD.billing,
          link_type: linkType.INTERNAL,
          icon: <Settings className="h-4 w-4" />,
        }
      : null;

  const allLinks = [
    ...defaultProfileDropdownLinks,
    ...(billingLink ? [billingLink] : []),
    ...additionalLinks,
  ];

  const helpLinks: ProfileDropdownLink[] = [
    {
      name: "Help Center",
      link: helpArticleUrls.home,
      link_type: linkType.EXTERNAL,
      icon: <CircleHelp className="h-4 w-4" />,
    },
  ];

  const handleLinkClick = (link: ProfileDropdownLink) => {
    if (link.onClick) {
      link.onClick();
    } else if (link.link_type === linkType.EXTERNAL && link.link) {
      window.open(link.link, "_blank");
    } else if (link.link) {
      navigate(link.link);
    }
  };

  return {
    displayName,
    displayEmail,
    allLinks,
    helpLinks,
    unreadCount,
    hasUnread,
    displayCount,
    handleLinkClick,
    handleLogOut,
  };
}

export type UserProfileMenu = ReturnType<typeof useUserProfileMenu>;

/** The account menu body, shared by the header avatar and the sidebar account row. */
export const UserProfileMenuItems = ({
  menu,
  showHelpLinks = false,
}: {
  menu: UserProfileMenu;
  showHelpLinks?: boolean;
}) => (
  <>
    <DropdownMenuLabel className="p-0 font-normal">
      <UserProfileInfo />
    </DropdownMenuLabel>
    <DropdownMenuSeparator />
    <DropdownMenuGroup>
      {menu.allLinks.map((menu_item, menu_k) => (
        <DropdownMenuItem key={menu_k} onClick={() => menu.handleLinkClick(menu_item)}>
          {menu_item.icon || <Settings className="h-4 w-4" />}
          <span className="flex-1">{menu_item.name}</span>
          {menu_item.name === "Notifications" && menu.hasUnread && (
            <span className="ml-2 flex h-5 min-w-[20px] items-center justify-center rounded-md border border-notification bg-notification/10 px-1.5 text-xs font-medium text-notification">
              {menu.displayCount}
            </span>
          )}
        </DropdownMenuItem>
      ))}
      <AppearanceThemeMenu />
    </DropdownMenuGroup>
    {showHelpLinks ? (
      <>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {menu.helpLinks.map((link, index) => (
            <DropdownMenuItem key={index} onClick={() => menu.handleLinkClick(link)}>
              {link.icon}
              {link.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </>
    ) : null}
    <DropdownMenuSeparator />
    <DropdownMenuItem onClick={menu.handleLogOut}>
      <LogOut className="h-4 w-4" />
      Log out
    </DropdownMenuItem>
  </>
);

export const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  className: _className,
  additionalLinks = [],
  showBilling = false,
  triggerClassName,
  contentClassName,
}) => {
  const menu = useUserProfileMenu({ additionalLinks, showBilling });
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          variant="ghost"
          size="icon"
          className={`rounded-full border-none bg-transparent shadow-none transition-all duration-300 hover:bg-muted/60 focus:bg-transparent focus:shadow-none focus:outline-none focus:ring-0 ${
            triggerClassName || ""
          }`}
        >
          <UserAvatar size="md" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={`w-56 rounded-lg ${contentClassName || ""}`}
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <UserProfileMenuItems menu={menu} showHelpLinks />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
